<template>
  <div
    class="fts-debug-panel"
    :class="{
      collapsed: collapsed && !inline && !floating,
      inline: inline && !floating,
      floating: floating,
      minimized: minimized,
      resizing: isResizing
    }"
    :style="floating && !minimized ? { width: floatingWidth + 'px' } : null"
    v-if="visible"
  >
    <button
      type="button"
      class="fts-debug-panel-minimized-action"
      @click="expand"
      title="Expand debug panel"
      v-if="minimized"
    >
      <font-awesome-icon :icon="['fas', 'expand-alt']" />
    </button>
    <div
      class="fts-debug-panel-resize-handle"
      v-if="floating && !minimized"
      @mousedown="startResize"
      title="Drag to resize"
    ></div>
    <div
      class="fts-debug-panel-header"
      @click="onHeaderClick"
      v-if="!minimized"
    >
      <div class="fts-debug-panel-title">
        <span class="fts-debug-panel-status" :class="'status-' + connectionState">
          <span class="fts-debug-panel-status-dot"></span>
          {{ connectionStateLabel }}
        </span>
        <span class="fts-debug-panel-name">Debug</span>
        <span class="fts-debug-panel-count" v-if="messages.length">{{ messages.length }}</span>
      </div>
      <div class="fts-debug-panel-actions" @click.stop>
        <button
          type="button"
          class="fts-debug-panel-action"
          @click="onReconnect"
          :disabled="isReconnecting || connectionState === 'connecting' || connectionState === 'reconnecting'"
          :title="connectionState === 'connected' ? 'Force reconnect' : 'Reconnect to server'"
        >
          <font-awesome-icon
            :icon="['fas', isReconnecting ? 'spinner' : 'sync-alt']"
            :spin="isReconnecting"
          />
        </button>
        <button
          type="button"
          class="fts-debug-panel-action fts-debug-panel-action--test"
          @click="onTest"
          :disabled="isTesting"
          title="Test run"
          v-if="testButton"
        >
          <font-awesome-icon
            :icon="['fas', isTesting ? 'spinner' : 'play']"
            :spin="isTesting"
          />
          <span>Test run</span>
        </button>
        <button
          type="button"
          class="fts-debug-panel-action"
          :class="{ active: autoScroll }"
          @click="autoScroll = !autoScroll"
          title="Auto scroll"
        >
          <font-awesome-icon :icon="['fas', 'arrow-down']" />
        </button>
        <button
          type="button"
          class="fts-debug-panel-action"
          @click="clear"
          title="Clear messages"
          :disabled="!messages.length"
        >
          <font-awesome-icon :icon="['fas', 'trash']" />
        </button>
        <button
          type="button"
          class="fts-debug-panel-action"
          @click="toggleCollapse"
          :title="collapsed ? 'Expand' : 'Collapse'"
          v-if="!inline && !floating"
        >
          <font-awesome-icon :icon="['fas', collapsed ? 'chevron-up' : 'chevron-down']" />
        </button>
        <button
          type="button"
          class="fts-debug-panel-action"
          @click="minimize"
          title="Minimize debug panel"
          v-if="floating"
        >
          <font-awesome-icon :icon="['fas', 'minus']" />
        </button>
        <button
          type="button"
          class="fts-debug-panel-action"
          @click="hide"
          title="Hide debug panel"
          v-if="!inline && !floating"
        >
          <font-awesome-icon :icon="['fas', 'times']" />
        </button>
      </div>
    </div>

    <div class="fts-debug-panel-body" v-show="!minimized && (inline || floating || !collapsed)">
      <div class="fts-debug-panel-filter">
        <input
          type="text"
          class="fts-debug-panel-search"
          placeholder="Filter messages..."
          v-model="filter"
        />
        <div class="fts-debug-panel-fontsize">
          <button
            type="button"
            class="fts-debug-panel-fontsize-btn"
            @click="decreaseFontSize"
            :disabled="fontSizePercent <= 60"
            title="Decrease font size"
          >
            <font-awesome-icon :icon="['fas', 'minus']" />
          </button>
          <span
            class="fts-debug-panel-fontsize-value"
            @click="resetFontSize"
            title="Reset to 100%"
          >{{ fontSizePercent }}%</span>
          <button
            type="button"
            class="fts-debug-panel-fontsize-btn"
            @click="increaseFontSize"
            :disabled="fontSizePercent >= 200"
            title="Increase font size"
          >
            <font-awesome-icon :icon="['fas', 'plus']" />
          </button>
        </div>
      </div>
      <div
        class="fts-debug-panel-messages fts-scrollbar"
        ref="messageList"
        :style="{ fontSize: (12 * fontSizePercent / 100) + 'px' }"
      >
        <div class="fts-debug-panel-empty" v-if="!filteredMessages.length">
          <template v-if="filter">No messages match your filter.</template>
          <template v-else>Waiting for debug messages...</template>
        </div>
        <div
          class="fts-debug-panel-message"
          :class="'level-' + msg.level"
          v-for="msg in filteredMessages"
          :key="msg.id"
        >
          <div class="fts-debug-panel-message-header" @click="toggleMessage(msg)">
            <span class="fts-debug-panel-message-time">{{ msg.time }}</span>
            <span class="fts-debug-panel-message-text">{{ msg.message }}</span>
            <span
              class="fts-debug-panel-message-progress"
              v-if="msg.total"
            >
              {{ msg.progress }} / {{ msg.total }}
            </span>
            <span
              class="fts-debug-panel-message-toggle"
              v-if="hasPayload(msg)"
            >
              <font-awesome-icon
                :icon="['fas', msg.expanded ? 'chevron-down' : 'chevron-right']"
              />
            </span>
          </div>
          <div
            class="fts-debug-panel-message-meta"
            v-if="msg.activityName || msg.moduleId || msg.entryId"
          >
            <span v-if="msg.activityName">activity: {{ msg.activityName }}</span>
            <span v-if="msg.moduleId">module: {{ msg.moduleId }}</span>
            <span v-if="msg.entryId">entry: {{ msg.entryId }}</span>
          </div>
          <div
            class="fts-debug-panel-message-payload"
            v-if="msg.expanded && hasPayload(msg)"
          >
            <button
              type="button"
              class="fts-debug-panel-message-payload-copy"
              @click.stop="copyPayload(msg)"
              :title="msg.copied ? 'Copied!' : 'Copy to clipboard'"
            >
              <font-awesome-icon :icon="['fas', msg.copied ? 'check' : 'copy']" />
              <span>{{ msg.copied ? 'Copied' : 'Copy' }}</span>
            </button>
            <vue-json-pretty
              v-if="isStructured(msg.payload)"
              :data="structuredPayload(msg.payload)"
              :deep="1"
              :show-length="true"
              :show-line="true"
            />
            <pre v-else class="fts-debug-panel-message-payload-raw">{{ formatPayload(msg.payload) }}</pre>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import signalr from "../store/signalr";
import VueJsonPretty from "vue-json-pretty";

const MAX_MESSAGES = 200;
let counter = 0;

const STATE_LABELS = {
  connected: "Connected",
  connecting: "Connecting",
  reconnecting: "Reconnecting",
  disconnected: "Disconnected",
  unknown: "Unknown"
};

export default {
  name: "FTSDebugPanel",
  components: {
    VueJsonPretty
  },
  props: {
    inline: { type: Boolean, default: false },
    floating: { type: Boolean, default: false },
    stateKey: String,
    testButton: { type: Boolean, default: false },
    isTesting: { type: Boolean, default: false }
  },
  data() {
    return {
      visible: true,
      collapsed: !this.inline && !this.floating,
      minimized: false,
      autoScroll: true,
      filter: "",
      connectionState: "unknown",
      isReconnecting: false,
      fontSizePercent: this.loadFontSize(),
      floatingWidth: this.loadFloatingWidth(),
      isResizing: false,
      messages: []
    };
  },
  computed: {
    connectionStateLabel() {
      return STATE_LABELS[this.connectionState] || STATE_LABELS.unknown;
    },
    minimizedStorageKey() {
      return this.stateKey ? "fts.debugPanel.minimized." + this.stateKey : null;
    },
    filteredMessages() {
      const f = (this.filter || "").trim().toLowerCase();
      if (!f) return this.messages;
      return this.messages.filter(m => {
        if (m.message && m.message.toLowerCase().indexOf(f) > -1) return true;
        if (m.activityName && m.activityName.toLowerCase().indexOf(f) > -1) return true;
        if (m.payloadText && m.payloadText.toLowerCase().indexOf(f) > -1) return true;
        return false;
      });
    }
  },
  watch: {
    floating(value) {
      if (value) {
        this.loadMinimized();
      } else {
        this.minimized = false;
      }
    },
    stateKey() {
      if (this.floating) {
        this.loadMinimized();
      }
    }
  },
  methods: {
    onHeaderClick() {
      if (this.inline || this.floating) return;
      this.toggleCollapse();
    },
    onTest() {
      if (this.isTesting) return;
      this.clear();
      this.$emit("test");
    },
    loadFloatingWidth() {
      try {
        const v = parseInt(window.localStorage.getItem("fts.debugPanel.floatingWidth"), 10);
        if (v >= 385 && v <= 2000) return v;
      } catch (err) {
        // ignore
      }
      return 480;
    },
    saveFloatingWidth() {
      try {
        window.localStorage.setItem("fts.debugPanel.floatingWidth", String(this.floatingWidth));
      } catch (err) {
        // ignore
      }
    },
    startResize(e) {
      e.preventDefault();
      e.stopPropagation();
      const startX = e.clientX;
      const startWidth = this.floatingWidth;
      this.isResizing = true;

      const onMove = (ev) => {
        const delta = startX - ev.clientX;
        const max = Math.max(385, window.innerWidth - 32);
        let w = startWidth + delta;
        if (w < 385) w = 385;
        if (w > max) w = max;
        this.floatingWidth = w;
      };
      const onUp = () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
        this.isResizing = false;
        this.saveFloatingWidth();
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    loadFontSize() {
      try {
        const v = parseInt(window.localStorage.getItem("fts.debugPanel.fontSize"), 10);
        if (v >= 60 && v <= 200) return v;
      } catch (err) {
        // ignore
      }
      return 100;
    },
    saveFontSize() {
      try {
        window.localStorage.setItem("fts.debugPanel.fontSize", String(this.fontSizePercent));
      } catch (err) {
        // ignore
      }
    },
    increaseFontSize() {
      this.fontSizePercent = Math.min(200, this.fontSizePercent + 10);
      this.saveFontSize();
    },
    decreaseFontSize() {
      this.fontSizePercent = Math.max(60, this.fontSizePercent - 10);
      this.saveFontSize();
    },
    resetFontSize() {
      this.fontSizePercent = 100;
      this.saveFontSize();
    },
    async onReconnect() {
      if (this.isReconnecting) return;
      this.isReconnecting = true;
      try {
        await signalr.reconnect();
      } catch (err) {
        this.logError(err);
      } finally {
        this.isReconnecting = false;
      }
    },
    toggleCollapse() {
      this.collapsed = !this.collapsed;
      if (!this.collapsed) {
        this.scrollToBottom();
      }
    },
    loadMinimized() {
      if (!this.minimizedStorageKey) {
        this.minimized = false;
        return;
      }
      try {
        this.minimized = window.localStorage.getItem(this.minimizedStorageKey) === "true";
      } catch (err) {
        this.minimized = false;
      }
    },
    saveMinimized() {
      if (!this.minimizedStorageKey) return;
      try {
        window.localStorage.setItem(this.minimizedStorageKey, String(this.minimized));
      } catch (err) {
        // ignore
      }
    },
    minimize() {
      this.minimized = true;
      this.saveMinimized();
    },
    expand() {
      this.minimized = false;
      this.saveMinimized();
      this.scrollToBottom();
    },
    hide() {
      this.visible = false;
    },
    show() {
      this.visible = true;
    },
    clear() {
      this.messages = [];
    },
    toggleMessage(msg) {
      if (!this.hasPayload(msg)) return;
      this.$set(msg, "expanded", !msg.expanded);
    },
    hasPayload(msg) {
      return msg.payload !== null && msg.payload !== undefined && msg.payload !== "";
    },
    isStructured(payload) {
      if (payload === null || payload === undefined) return false;
      if (typeof payload === "object") return true;
      if (typeof payload === "string") {
        const trimmed = payload.trim();
        if (
          (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
          (trimmed.startsWith("[") && trimmed.endsWith("]"))
        ) {
          try {
            JSON.parse(trimmed);
            return true;
          } catch (err) {
            return false;
          }
        }
      }
      return false;
    },
    structuredPayload(payload) {
      if (typeof payload === "string") {
        try {
          return JSON.parse(payload);
        } catch (err) {
          return payload;
        }
      }
      return payload;
    },
    async copyPayload(msg) {
      const text = this.formatPayload(msg.payload);
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.left = "-9999px";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        }
        this.$set(msg, "copied", true);
        setTimeout(() => this.$set(msg, "copied", false), 1500);
      } catch (err) {
        console.error("copy payload failed", err);
      }
    },
    formatPayload(payload) {
      if (payload === null || payload === undefined) return "";
      if (typeof payload === "string") return payload;
      try {
        return JSON.stringify(payload, null, 2);
      } catch (err) {
        return String(payload);
      }
    },
    payloadToText(payload) {
      if (payload === null || payload === undefined) return "";
      if (typeof payload === "string") return payload;
      try {
        return JSON.stringify(payload);
      } catch (err) {
        return "";
      }
    },
    pushMessage(partial) {
      counter += 1;
      const msg = Object.assign(
        {
          id: counter,
          time: this.timestamp(),
          message: "(no message)",
          payload: null,
          payloadText: "",
          progress: 0,
          total: 0,
          activityName: "",
          moduleId: "",
          entryId: "",
          level: "info",
          expanded: false
        },
        partial
      );
      msg.payloadText = this.payloadToText(msg.payload);

      this.messages.push(msg);
      if (this.messages.length > MAX_MESSAGES) {
        this.messages.splice(0, this.messages.length - MAX_MESSAGES);
      }

      if (this.autoScroll && !this.collapsed) {
        this.scrollToBottom();
      }
    },
    onScriptDebug(data) {
      let entry;
      if (typeof data === "string") {
        entry = { Message: data };
      } else if (data && typeof data === "object") {
        entry = data;
      } else {
        entry = { Message: String(data) };
      }

      const message = entry.Message || entry.message || "";
      const payload = entry.Payload !== undefined ? entry.Payload : entry.payload;
      const progress = entry.Progress !== undefined ? entry.Progress : entry.progress;
      const total = entry.Total !== undefined ? entry.Total : entry.total;
      const activityName = entry.ActivityName || entry.activityName;
      const moduleId = entry.ModuleId || entry.moduleId;
      const entryId = entry.EntryId || entry.entryId;

      const lower = (message || "").toLowerCase();
      let level = "info";
      if (lower.indexOf("error") > -1 || lower.indexOf("exceeded") > -1) {
        level = "error";
      } else if (lower.indexOf("warn") > -1) {
        level = "warn";
      }

      this.pushMessage({
        message: message || "(no message)",
        payload: payload,
        progress: progress || 0,
        total: total || 0,
        activityName: activityName || "",
        moduleId: moduleId || "",
        entryId: entryId || "",
        level: level
      });
    },
    logError(err) {
      const response = err && err.response;
      const data = response && response.data;
      const status = response && response.status ? " (HTTP " + response.status + ")" : "";

      let title = "";
      let payload = null;

      if (data && typeof data === "object") {
        let cursor = data;
        while (cursor && cursor.innerException) {
          cursor = cursor.innerException;
        }
        title =
          (cursor && (cursor.exceptionMessage || cursor.message)) ||
          data.exceptionMessage ||
          data.message ||
          "";
        payload = data;
      } else if (data) {
        title = String(data);
        payload = data;
      } else if (err && err.message) {
        title = err.message;
        payload = err.stack || null;
      } else {
        title = "Test run failed";
        payload = err ? String(err) : null;
      }

      this.pushMessage({
        message: "ERROR: " + (title || "Test run failed") + status,
        payload: payload,
        level: "error",
        expanded: true
      });
    },
    onConnectionState(state) {
      if (state) {
        this.connectionState = state;
      }
    },
    onSignalrError(err) {
      this.onScriptDebug({
        Message: "HUB ERROR",
        Payload: err && err.message ? err.message : String(err)
      });
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const list = this.$refs.messageList;
        if (list) list.scrollTop = list.scrollHeight;
      });
    },
    timestamp() {
      const d = new Date();
      const pad = n => (n < 10 ? "0" + n : "" + n);
      return (
        pad(d.getHours()) +
        ":" +
        pad(d.getMinutes()) +
        ":" +
        pad(d.getSeconds())
      );
    }
  },
  mounted() {
    if (this.floating) {
      this.loadMinimized();
    }
    window.eventBus.$on("signalr.scriptDebug", this.onScriptDebug);
    window.eventBus.$on("signalr.connectionState", this.onConnectionState);
    window.eventBus.$on("signalr.error", this.onSignalrError);
  },
  beforeDestroy() {
    window.eventBus.$off("signalr.scriptDebug", this.onScriptDebug);
    window.eventBus.$off("signalr.connectionState", this.onConnectionState);
    window.eventBus.$off("signalr.error", this.onSignalrError);
  }
};
</script>

<style lang="scss">
.fts-debug-panel {
  position: fixed;
  right: 1rem;
  bottom: 1rem;
  width: 420px;
  max-width: calc(100vw - 2rem);
  background: #1e1e1e;
  color: #e6e6e6;
  border-radius: var(--inistate-rounded-8);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
  font-family: "SFMono-Regular", Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  z-index: 9998;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.collapsed {
    width: auto;
    min-width: 220px;
  }

  &.inline {
    position: relative;
    right: auto;
    bottom: auto;
    width: 100%;
    max-width: none;
    box-shadow: none;
    border: 1px solid #2a2a2a;
    z-index: auto;

    .fts-debug-panel-header { cursor: default; }
    .fts-debug-panel-body { max-height: 320px; }
  }

  &.floating {
    position: fixed;
    right: 1rem;
    bottom: 1rem;
    left: auto;
    max-width: calc(100vw - 2rem);
    z-index: 1041;

    .fts-debug-panel-header { cursor: default; }
    .fts-debug-panel-body { max-height: 40vh; }
  }

  &.minimized {
    width: auto;
    right: 0.75rem;
    bottom: 0.25rem;
    max-width: none;
    background: transparent;
    border-radius: 0;
    box-shadow: none;
    overflow: visible;
  }

  &.resizing {
    user-select: none;
  }

  .fts-debug-panel-minimized-action {
    position: relative;
    width: 32px;
    height: 32px;
    border: 1px solid transparent;
    border-radius: var(--inistate-rounded-8);
    background-color: transparent;
    color: #1e1e1e;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    transition: background-color, color ease 0.3s;

    &:hover {
      border-color: #1e1e1e;
      background-color: #1e1e1e;
      color: #e6e6e6;
    }
  }

  .fts-debug-panel-resize-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 6px;
    cursor: ew-resize;
    background: transparent;
    z-index: 2;

    &:hover,
    .fts-debug-panel.resizing & {
      background: rgba(79, 195, 247, 0.35);
    }
  }

  .fts-debug-panel-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 0.75rem;
    background: #111;
    cursor: pointer;
    user-select: none;
    border-bottom: 1px solid #2a2a2a;
  }

  .fts-debug-panel-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
  }

  .fts-debug-panel-name {
    font-weight: 600;
    color: #fff;
  }

  .fts-debug-panel-count {
    background: #3a3a3a;
    color: #fff;
    border-radius: 10px;
    padding: 0 0.4rem;
    font-size: 11px;
    line-height: 16px;
    min-width: 18px;
    text-align: center;
  }

  .fts-debug-panel-status {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.1rem 0.45rem;
    border-radius: 10px;
    font-size: 11px;
    background: #2a2a2a;
    color: #ddd;

    .fts-debug-panel-status-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #888;
    }

    &.status-connected .fts-debug-panel-status-dot { background: #4caf50; }
    &.status-connecting .fts-debug-panel-status-dot,
    &.status-reconnecting .fts-debug-panel-status-dot {
      background: #ffb300;
      animation: fts-debug-pulse 1s ease-in-out infinite;
    }
    &.status-disconnected .fts-debug-panel-status-dot { background: #f44336; }
  }

  .fts-debug-panel-actions {
    display: flex;
    align-items: center;
    gap: 0.25rem;
  }

  .fts-debug-panel-action {
    background: transparent;
    border: none;
    color: #bbb;
    width: 24px;
    height: 24px;
    border-radius: 4px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;

    &:hover:not(:disabled) {
      background: #2a2a2a;
      color: #fff;
    }
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
    &.active {
      color: #4fc3f7;
    }

    &.fts-debug-panel-action--test {
      width: auto;
      height: 26px;
      padding: 0 0.6rem;
      gap: 0.35rem;
      background: #ff9800;
      color: #1e1e1e;
      font-weight: 600;
      margin-right: 0.25rem;

      &:hover:not(:disabled) {
        background: #ffa726;
        color: #1e1e1e;
      }
    }
  }

  .fts-debug-panel-body {
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 50vh;
  }

  .fts-debug-panel-filter {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid #2a2a2a;
  }

  .fts-debug-panel-search {
    flex: 1;
    min-width: 0;
    background: #2a2a2a;
    border: 1px solid #333;
    border-radius: 4px;
    color: #e6e6e6;
    padding: 0.3rem 0.5rem;
    font-size: 12px;
    font-family: inherit;
    outline: none;

    &:focus {
      border-color: #4fc3f7;
    }
  }

  .fts-debug-panel-fontsize {
    display: inline-flex;
    align-items: center;
    gap: 0.15rem;
    background: #2a2a2a;
    border: 1px solid #333;
    border-radius: 4px;
    padding: 0.1rem 0.2rem;
    flex-shrink: 0;

    .fts-debug-panel-fontsize-btn {
      background: transparent;
      border: none;
      color: #bbb;
      width: 20px;
      height: 20px;
      border-radius: 3px;
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      font-size: 10px;

      &:hover:not(:disabled) {
        background: #3a3a3a;
        color: #fff;
      }
      &:disabled {
        opacity: 0.4;
        cursor: not-allowed;
      }
    }

    .fts-debug-panel-fontsize-value {
      min-width: 34px;
      text-align: center;
      color: #ddd;
      font-size: 11px;
      cursor: pointer;
      user-select: none;

      &:hover { color: #fff; }
    }
  }

  .fts-debug-panel-messages {
    flex: 1;
    overflow-y: auto;
    padding: 0.25rem 0;
  }

  .fts-debug-panel-empty {
    padding: 1rem;
    text-align: center;
    color: #666;
  }

  .fts-debug-panel-message {
    padding: 0.35rem 0.75rem;
    border-bottom: 1px solid #262626;
    word-break: break-word;

    &.level-error {
      background: rgba(244, 67, 54, 0.08);
      .fts-debug-panel-message-text { color: #ff8a80; }
    }
    &.level-warn {
      background: rgba(255, 179, 0, 0.08);
      .fts-debug-panel-message-text { color: #ffd54f; }
    }
  }

  .fts-debug-panel-message-header {
    display: flex;
    align-items: baseline;
    gap: 0.5rem;
    cursor: pointer;
  }

  .fts-debug-panel-message-time {
    color: #888;
    font-size: 0.917em;
    flex-shrink: 0;
  }

  .fts-debug-panel-message-text {
    flex: 1;
    color: #e6e6e6;
    white-space: pre-wrap;
  }

  .fts-debug-panel-message-progress {
    color: #4fc3f7;
    font-size: 0.917em;
    flex-shrink: 0;
  }

  .fts-debug-panel-message-toggle {
    color: #888;
    font-size: 0.833em;
    flex-shrink: 0;
  }

  .fts-debug-panel-message-meta {
    margin-top: 0.25rem;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    font-size: 0.833em;
    color: #888;
  }

  .fts-debug-panel-message-payload {
    position: relative;
    margin: 0.4rem 0 0;
    padding: 0.5rem;
    background: #0d0d0d;
    border-radius: 4px;
    color: #cfcfcf;
    font-size: 0.917em;
    max-height: 320px;
    overflow: auto;
    word-break: break-word;

    .fts-debug-panel-message-payload-copy {
      position: sticky;
      float: right;
      top: 0;
      right: 0;
      display: inline-flex;
      align-items: center;
      gap: 0.3rem;
      background: #2a2a2a;
      color: #bbb;
      border: 1px solid #333;
      border-radius: 4px;
      padding: 0.15rem 0.45rem;
      font-size: 0.909em;
      font-family: inherit;
      cursor: pointer;
      z-index: 1;

      &:hover {
        background: #3a3a3a;
        color: #fff;
      }
    }

    .fts-debug-panel-message-payload-raw {
      margin: 0;
      color: inherit;
      font-family: inherit;
      font-size: inherit;
      white-space: pre-wrap;
      word-break: break-word;
    }

    .vjs-tree {
      font-family: "SFMono-Regular", Menlo, Monaco, Consolas, monospace;
      font-size: inherit;
      color: #cfcfcf;

      .vjs-key { color: #82aaff; }
      .vjs-value-string { color: #c3e88d; }
      .vjs-value-number { color: #f78c6c; }
      .vjs-value-boolean { color: #ffcb6b; }
      .vjs-value-null { color: #ff5370; }
      .vjs-tree-node { line-height: 1.5; }
      .vjs-tree-node:hover { background: rgba(255, 255, 255, 0.04); }
      .vjs-tree .vjs-tree { border-left: 1px dashed #333; }
    }
  }
}

@keyframes fts-debug-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
</style>
